import { useQuery } from "@tanstack/react-query";
import { getDocs, getFirestore, orderBy, query } from "firebase/firestore";
import type { FC } from "react";
import { queryKeys } from "../../../queries/queryKeys";
import { formatearNumeros } from "../../../utils/formatters";
import { crearCierresCajaCollectionRef } from "../../../utils/rutaCierresCaja";
import type { CierreCaja, TablaCierresCajaProps } from "../types";

type CierreCajaTabla = CierreCaja & {
  id: string;
};

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const renderMontoOpcional = (value: number | string | null | undefined): string => {
  if (value === undefined || value === null) {
    return "-";
  }

  const numero = normalizarNumero(value);

  return numero > 0 ? `R$${formatearNumeros(numero)}` : "-";
};

const fetchCierresCaja = async (adminUid: string, rutaUid: string): Promise<CierreCajaTabla[]> => {
  const db = getFirestore();
  const cierresCajaRef = crearCierresCajaCollectionRef(db, adminUid, rutaUid);
  const cierresCajaQuery = query(cierresCajaRef, orderBy("fechaHoraGenerado", "desc"));
  const cierresCajaSnapshot = await getDocs(cierresCajaQuery);

  return cierresCajaSnapshot.docs.map((cierreDoc) => ({
    id: cierreDoc.id,
    ...(cierreDoc.data() as CierreCaja),
  }));
};

export const TablaCierresCaja: FC<TablaCierresCajaProps> = ({ formatDate, usuarioRuta }) => {
  const adminUid = usuarioRuta?.adminUid;
  const rutaUid = usuarioRuta?.uid;

  // El cierre de "hoy" puede aparecer por el cron automatico a las 23:30 sin
  // ninguna accion del cliente; staleTime corto + refetchOnWindowFocus evita
  // que la tabla quede mostrando datos de ayer si el usuario vuelve a la
  // pestaña despues de esa hora. El historial de dias pasados es inmutable,
  // pero esta vista no los separa, asi que se aplica el mismo criterio corto
  // a toda la lista en vez de sobre-disenar dos queries para un solo listado.
  const { data: cierresCaja = [], isLoading } = useQuery({
    enabled: Boolean(adminUid && rutaUid),
    gcTime: 60 * 60 * 1000,
    queryFn: () => fetchCierresCaja(adminUid as string, rutaUid as string),
    queryKey: queryKeys.cierresCajaHistorial(adminUid, rutaUid),
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  return (
    <table className="min-w-full mt-2 bg-white">
      <thead>
        <tr className="bg-[#8131bd] text-white uppercase text-sm">
          <th className="whitespace-nowrap border border-black px-2 py-1">#</th>
          <th className="whitespace-nowrap border border-black px-2 py-1">Saldo actual</th>
          <th className="whitespace-nowrap border border-black px-2 py-1">Cobros del dia</th>
          <th className="whitespace-nowrap border border-black px-2 py-1">Prestamos del dia</th>
          <th className="whitespace-nowrap border border-black px-2 py-1">Gastos del dia</th>
          <th className="whitespace-nowrap border border-black px-2 py-1">Ingresos</th>
          <th className="whitespace-nowrap border border-black px-2 py-1">Retiros</th>
          <th className="whitespace-nowrap border border-black px-2 py-1">Fecha y hora</th>
        </tr>
      </thead>
      <tbody>
        {cierresCaja.length > 0 ? (
          cierresCaja.map((item, index) => (
            <tr
              key={item.id}
              className={`${
                index % 2 === 0 ? "bg-gray-300" : "bg-gray-100"
              } border border-black font-semibold uppercase text-sm`}
            >
              <td className="whitespace-nowrap border border-black text-center px-2 py-1">
                {index + 1}
              </td>
              <td className="whitespace-nowrap border border-black text-center px-2 py-1">
                {`R$${formatearNumeros(normalizarNumero(item.saldoActual))}`}
              </td>
              <td className="whitespace-nowrap border border-black text-center px-2 py-1 text-green-700">
                {`R$${formatearNumeros(normalizarNumero(item.cobrosDelDia))}`}
              </td>
              <td className="whitespace-nowrap border border-black text-center px-2 py-1 text-red-600">
                {`R$${formatearNumeros(normalizarNumero(item.prestamosDelDia))}`}
              </td>
              <td className="whitespace-nowrap border border-black text-center px-2 py-1 text-red-600">
                {`R$${formatearNumeros(normalizarNumero(item.gastosDelDia))}`}
              </td>
              <td className="whitespace-nowrap border border-black text-center px-2 py-1 text-green-700">
                {renderMontoOpcional(item.ingresosDelDia)}
              </td>
              <td className="whitespace-nowrap border border-black text-center px-2 py-1 text-red-600">
                {renderMontoOpcional(item.retirosDelDia)}
              </td>
              <td className="whitespace-nowrap border border-black text-center px-2 py-1">
                {item.fechaHoraGenerado ? formatDate(item.fechaHoraGenerado) : "Sin fecha"}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={8} className="border border-black px-2 py-2 text-center">
              {isLoading ? "Cargando cierres de caja..." : "No hay datos disponibles."}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};
